import Card from '@rio-cloud/rio-uikit/Card';
import EmptyState from '@rio-cloud/rio-uikit/EmptyState';
import { useEffect } from 'react';
import { connect } from 'react-redux';

import type { RootState } from '../../../configuration/setup/store';
import { getHasError, getIsLoading } from '../api/redux/api.redux';
import { ApiCalls } from '../api/redux/types';
import { safeDataLayerPush } from '../common/utils/googleTagManagerWrapper';
import {
    CheckoutSteps,
    type GACheckoutStepData,
    getGACheckoutStepData,
    getGAEventCheckoutProgress,
} from './checkoutGTMUtils';
import { BusCustomerHintContainer } from './components/BusCustomerHintContainer';
import { CheckoutStepHeader } from './components/CheckoutStepHeader';
import { ExternalAdminHintContainer } from './components/ExternalAdminHintContainer';
import { NextButton } from './components/NavigationButtons';
import { VariantSelectionContainer } from './components/VariantSelectionContainer';
import { getIsCheckoutLocked, getSelectedProductLevel } from './redux/checkout.redux';
import { ProductTypeAwareFormattedMessage } from './resourceSelection/ProductTypeAwareFormattedMessage';
import { ResourceSelectionContainer } from './resourceSelection/ResourceSelectionContainer';
import { getAvailableResources, getSelectedResourceIds } from './resourceSelection/redux/resourceSelection.redux';

interface Props {
    nextButtonDisabled: boolean;
    nextPath: string;
    gAData: GACheckoutStepData;
    isResourceSelectionLocked: boolean;
    hasAvailableResources: boolean;
}

const CheckoutResourceSelection = ({
    nextButtonDisabled,
    nextPath,
    gAData,
    isResourceSelectionLocked,
    hasAvailableResources,
}: Props) => {
    useEffect(() => {
        if (gAData.product) {
            safeDataLayerPush(() => getGAEventCheckoutProgress(gAData));
        }
    }, [gAData.product]);

    return (
        <section className='padding-bottom-25'>
            <CheckoutStepHeader
                title={
                    <ProductTypeAwareFormattedMessage id={isResourceSelectionLocked ? 'headlineLocked' : 'headline'} />
                }
            />
            {hasAvailableResources ? (
                <Card className='assetSelection-panel' padding={25} shadow='subtle'>
                    <VariantSelectionContainer />
                    <div className='display-flex justify-content-center'>
                        <ResourceSelectionContainer />
                    </div>
                    <div>
                        <ExternalAdminHintContainer />
                        <BusCustomerHintContainer />
                        <div className='display-flex justify-content-end'>
                            <NextButton disabled={nextButtonDisabled} nextPath={nextPath} />
                        </div>
                    </div>
                </Card>
            ) : (
                <EmptyState
                    headline={<ProductTypeAwareFormattedMessage id='noResources' />}
                    message={<ExternalAdminHintContainer />}
                    outerClassName='margin-top-15pct'
                />
            )}
        </section>
    );
};
const mapStateToProps = (state: RootState) => ({
    nextButtonDisabled: getSelectedResourceIds(state).length === 0 || getIsLoading(state, ApiCalls.RESOURCE_SELECTION),
    gAData: getGACheckoutStepData(state, CheckoutSteps.TO_BILLING_ADDRESS),
    isResourceSelectionLocked: getIsCheckoutLocked(state),
    selectedProductLevel: getSelectedProductLevel(state),
    hasAvailableResources:
        getIsLoading(state, ApiCalls.RESOURCE_SELECTION) ||
        getHasError(state, ApiCalls.RESOURCE_SELECTION) ||
        getAvailableResources(state).length > 0,
});

export const CheckoutResourceSelectionContainer = connect(mapStateToProps)(CheckoutResourceSelection);
